import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flag, Clock, Trophy, BookOpen, CloudSun } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import ProgramBuyModal from "@/components/ProgramBuyModal";

const FALLBACK_EVENTS = [
  { date: "2026-11-08", title: "AASA Season Opener", classes: "Sports Sedans · 1200 Juniors · V8 Non Winged · F500 S/J Non Winged · Open Juniors · Standard Saloons · Standard Juniors · 1500 Juniors", tag: "AASA Sunday" },
  { date: "2027-01-31", title: "Prelude to the Victorian Junior Sedan Title", classes: "SSA Junior Sedans (Top and New Stars) · Street Stocks · Standard Saloons · Production Sedans · Limited Sporties · More Classes TBC", tag: "SSA Event" },
  { date: "2027-02-21", title: "JQMV State Series Round", classes: "Junior Quarter Midgets — All Classes", tag: "JQMV" },
  { date: "2027-02-28", title: "AASA Summer Round", classes: "Sports Sedans · 1200 Juniors · V8 Non Winged · F500 S/J Non Winged TBC · Open Juniors · Standard Saloons · Standard Juniors · 1500 Juniors", tag: "AASA Sunday" },
  { date: "2027-03-06", title: "Victorian Junior Sedan Title — Night 1", classes: "SDAV Hot Rods · Lightning Sprints · Street Stocks (SSV Series)", tag: "VIC Title" },
  { date: "2027-03-07", title: "Victorian Junior Sedan Title — Night 2", classes: "Super Rods / SDAV Hot Rods · Lightning Sprints · Street Stocks (SSV Series)", tag: "VIC Title" },
  { date: "2027-04-10", title: "Modified Production Feature", classes: "F500 Senior/Junior · Modified Production Feature · Standard Saloons · Vintage Hot Rods · Vintage Sedans · Super Mods · Sprintcars", tag: "Feature Night" },
  { date: "2027-04-11", title: "JQMV Club Day", classes: "Junior Quarter Midgets — All Classes", tag: "JQMV" },
  { date: "2027-04-24", title: "Victorian Super Rod Title", classes: "Compact Speedcars · Sports Sedans Feature · SSA 1200 Juniors · More Classes TBC — ANZAC Day Weekend", tag: "VIC Title" },
  { date: "2027-04-25", title: "JQMV Club Day", classes: "Junior Quarter Midgets — All Classes — ANZAC Day", tag: "JQMV" },
  { date: "2027-05-01", title: "Pink Night", classes: "Street Stocks (Hanno Tribute) · Standard Saloons (Golden Helmet) · Super Rods (Col Grist Memorial) · Wingless Sprints (Stony Point Feature) · Production Sedans (SSV Series) · More Classes TBC", tag: "Signature" },
  { date: "2027-05-09", title: "JQMV Country Cup Round", classes: "Junior Quarter Midgets — All Classes", tag: "JQMV" },
  { date: "2027-05-29", title: "Grass Roots Day", classes: "Street Stocks Co-Driver · Standard Saloons · SSA 1200 Juniors · Lightning/Mini Sprints · Divi 2 Hot Rods · GP Midgets · Limited Sporties · More Classes TBC", tag: "Season Finale" },
];

const fmtDay = (iso) => String(parseInt(iso.slice(8, 10), 10)).padStart(2, "0");
const fmtMonth = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { month: "short", year: "numeric" }).toUpperCase();

const nomClosed = (e) =>
  e.cancelled || e.nominations_open === false || (e.nomination_cutoff && new Date(e.nomination_cutoff) < new Date());

export default function Events({ onNominate }) {
  const [events, setEvents] = useState(FALLBACK_EVENTS);
  const [posters, setPosters] = useState({ main: null, jqmv: null });
  const [programEvent, setProgramEvent] = useState(null);
  const [forecast, setForecast] = useState({});
  const [admission, setAdmission] = useState(null);
  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local

  useEffect(() => {
    fetch(`${API}/events`)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d) && d.length) setEvents(d);
      })
      .catch(() => {});
    fetch(`${API}/settings/posters`)
      .then((r) => r.json())
      .then((d) => setPosters({ main: d.main || null, jqmv: d.jqmv || null }))
      .catch(() => {});
    fetch(`${API}/weather/forecast`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.days) setForecast(Object.fromEntries(d.days.map((day) => [day.date, day])));
      })
      .catch(() => {});
    fetch(`${API}/settings/admission`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setAdmission(d))
      .catch(() => {});
  }, []);

  return (
    <section id="schedule" className="border-y border-clay-border bg-clay-surface/40">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 lg:py-28">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4"
        >
          2026/27 Season Calendar
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.1 }}
          className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-none mb-4"
        >
          Race Calendar
        </motion.h2>
        {admission && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: 0.15 }}
            data-testid="admission-prices"
            className="border border-clay-border bg-clay-card/60 px-6 py-5 mb-8"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-clay-gold mb-4">Gate Admission</p>
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              {[
                ["General Admission", admission.general, "admission-general"],
                ["Member / Concession", admission.member_concession, "admission-concession"],
                ["Kids Under 15", admission.kids_under_15, "admission-under-15"],
                ["Kids Under 10", admission.kids_under_10, "admission-under-10"],
                ["Family (2 Adults + 2 Kids)", admission.family, "admission-family"],
              ].map(([label, price, testid]) => (
                <div key={testid} data-testid={testid}>
                  <p className="font-display font-black text-2xl sm:text-3xl text-clay-text tabular-nums">
                    {price === 0 ? "FREE" : `$${price % 1 ? price.toFixed(2) : price}`}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-0.5">{label}</p>
                </div>
              ))}
            </div>
            {admission.note && (
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-4">{admission.note}</p>
            )}
          </motion.div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-start">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-clay-muted max-w-2xl lg:col-span-4"
          >
            First track to host the brand-new V8 Non Winged Sprint Cars, two AASA Sunday meetings, the return of the
            Modified Productions, and four Junior Quarter Midget rounds. Dates and classes subject to change.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-4"
          >
            <figure data-testid="events-poster" className="border border-clay-border bg-clay-card p-3 group">
              <div className="overflow-hidden">
                <img
                  src={posters.main || "/poster-2026-27.jpg"}
                  alt="Official 2026/27 season calendar poster"
                  className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <figcaption className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mt-3 px-1 pb-1">
                Official 2026/27 Season Calendar
              </figcaption>
            </figure>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="lg:col-span-4"
          >
            <figure data-testid="events-poster-jqmv" className="border border-clay-border bg-clay-card p-3 group h-full">
              <div className="overflow-hidden">
                <img
                  src={posters.jqmv || "/jqmv-poster-2026-27.jpg"}
                  alt="JQMV Junior Quarter Midgets 2026/27 calendar poster"
                  className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <figcaption className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mt-3 px-1 pb-1">
                JQMV Junior Quarter Midgets 2026/27 — Track Champions awarded for all classes at season's end
              </figcaption>
            </figure>
          </motion.div>
        </div>

        <div className="border-t border-clay-border">
          {events.map((e, i) => (
            <motion.div
              key={e.title}
              data-testid="event-card"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="group relative grid grid-cols-[auto_1fr] sm:grid-cols-[120px_1fr_auto] items-center gap-5 sm:gap-8 border-b border-clay-border py-6 hover:bg-clay-card/60 transition-colors duration-300 px-2 sm:px-4"
            >
              <div className="text-center">
                <div className="font-display font-black text-4xl sm:text-5xl text-clay-orange leading-none">{fmtDay(e.date)}</div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-clay-muted mt-1">{fmtMonth(e.date)}</div>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display font-bold uppercase text-xl sm:text-2xl tracking-tight group-hover:text-clay-gold transition-colors">
                    {e.title}
                  </h3>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] border border-clay-gold/40 text-clay-gold px-2 py-0.5 flex items-center gap-1">
                    {e.tag.includes("Title") || e.tag === "Signature" ? <Trophy className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {e.tag}
                  </span>
                </div>
                <p className="text-clay-muted text-sm mt-1.5">{e.classes}</p>
                {e.date >= todayStr && (
                  <p data-testid={`event-forecast-${i}`} className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-clay-gold/80 mt-2">
                    <CloudSun className="w-3.5 h-3.5 shrink-0" />
                    {forecast[e.date]
                      ? `Forecast: ${forecast[e.date].conditions} · ${Math.round(forecast[e.date].min_temp_c)}–${Math.round(forecast[e.date].max_temp_c)}°C · ${forecast[e.date].rain_chance_percent ?? 0}% rain`
                      : "Forecast available closer to race day"}
                  </p>
                )}
              </div>
              <div className="hidden sm:flex flex-col gap-2 items-end">
                {(() => {
                  const closed = nomClosed(e);
                  return (
                    <button
                      data-testid={`event-nominate-${i}`}
                      disabled={closed}
                      onClick={() => onNominate?.(e.id)}
                      className={
                        closed
                          ? "flex items-center gap-2 border border-clay-border text-clay-muted/50 font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 cursor-not-allowed"
                          : "flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-all duration-300 hover:-translate-y-0.5"
                      }
                    >
                      <Flag className="w-4 h-4" /> {e.cancelled ? "Event Cancelled" : closed ? "Noms Closed" : "Nominate"}
                    </button>
                  );
                })()}
                {e.id && e.program_available && (
                  <button
                    data-testid={`event-program-${i}`}
                    onClick={() => setProgramEvent(e)}
                    className="flex items-center gap-2 border border-clay-border hover:border-clay-orange text-clay-muted hover:text-clay-text font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors duration-300"
                  >
                    <BookOpen className="w-4 h-4" /> Program
                  </button>
                )}
              </div>
            {e.cancelled && (
              <div className="absolute inset-0 z-20 bg-clay-bg/60 flex items-center justify-center pointer-events-none">
                <span
                  data-testid={`event-cancelled-banner-${i}`}
                  className="-rotate-6 bg-red-600 text-white font-display font-black uppercase tracking-[0.25em] text-base sm:text-lg px-8 py-3 border-2 border-red-400 shadow-lg shadow-black/60"
                >
                  Event Cancelled
                </span>
              </div>
            )}
            </motion.div>
          ))}
        </div>
      </div>
      {programEvent && (
        <ProgramBuyModal
          eventId={programEvent.id}
          eventTitle={programEvent.title}
          price="$2"
          open={!!programEvent}
          setOpen={() => setProgramEvent(null)}
        />
      )}
    </section>
  );
}
